// Onboarding helpers — local-storage persistence, server-sync hooks,
// landing-page launch helper. Phase F: frontend stage-seed data has been
// REMOVED (server is now the source of truth for tasks/roadmap/vault seeds).
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "./store";
import { useAuth } from "./authContext";
import api from "./api";

export const ONBOARDING_KEY = "ideaai_onboarding_v1";

export const STAGE_LABELS = {
  idea: "Idea",
  preseed: "Pre-Seed",
  seed: "Seed",
  seriesA: "Series A+",
};

export const STAGE_TAGLINE = {
  idea: "Pre-incorporation · validating",
  preseed: "Building MVP · pre-seed",
  seed: "Scaling · seed-funded",
  seriesA: "Expanding · series A+",
};

export function loadOnboarding() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ONBOARDING_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveOnboarding(data) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ONBOARDING_KEY, JSON.stringify(data));
  } catch {
    /* swallow */
  }
}

export function clearOnboardingStorage() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(ONBOARDING_KEY);
  } catch {
    /* swallow */
  }
}

// Build the AppShell's left-rail "company chip" from a server- or local-shape
// onboarding doc. Snake_case (server) wins; falls back to camelCase (legacy).
export function deriveProfile(onboarding) {
  if (!onboarding) return null;
  const stage = onboarding.stage || "preseed";
  const stageLabel = STAGE_LABELS[stage] || "Pre-Seed";
  const company =
    onboarding.company_name || onboarding.company || "Untitled Co.";
  const industry =
    onboarding.industry ||
    (onboarding.company_tagline || "").split(" · ")[0] ||
    "AI Productivity OS";
  const tagline = `${industry} · ${STAGE_TAGLINE[stage] || "Pre-seed"}`;
  return {
    name: company,
    tagline,
    stage: stageLabel.toUpperCase(),
    country: onboarding.country || "US",
    incorporationDate: "2026-03-01",
    burn: 48000,
    mrr: 12000,
    teamSize: 1,
  };
}

// React hook used by every "Launch boardroom" CTA on the landing page.
export function useLaunchBoardroom() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const onboarding = useStore((s) => s.onboarding);
  const openOnboarding = useStore((s) => s.openOnboarding);
  return () => {
    if (!isAuthenticated) {
      navigate("/signup?from=" + encodeURIComponent("/boardroom"));
      return;
    }
    if (onboarding) {
      navigate("/boardroom");
    } else {
      openOnboarding({ then: () => navigate("/boardroom") });
    }
  };
}

// Sync the onboarding store with the server whenever auth state changes.
// Mounted once at app root via AuthProvider's children. Sets
// `onboardingHydrated` after every check so AppShell can render confidently.
export function useOnboardingSync() {
  const { isAuthenticated, isLoading } = useAuth();
  const hydrate = useStore((s) => s.hydrateOnboardingFromServer);
  const migrate = useStore((s) => s.migrateOnboardingToServer);
  const setHydrated = useStore((s) => s.setOnboardingHydrated);
  const localOnboarding = useStore((s) => s.onboarding);
  const clearOnboarding = useStore((s) => s.clearOnboarding);

  useEffect(() => {
    if (isLoading) return;
    let cancelled = false;
    setHydrated(false);
    (async () => {
      if (!isAuthenticated) {
        if (!cancelled) setHydrated(true);
        return;
      }
      try {
        const { data } = await api.get("/api/onboarding");
        if (!cancelled) hydrate(data);
      } catch {
        // 404 = no server profile yet
        if (localOnboarding) {
          await migrate();
        } else if (!cancelled) {
          clearOnboarding();
        }
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-run only when auth flips
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isLoading]);
}
